<template>
  <div class="point-section fixed-input-wrapper" :class="{ __expand: !isInitial }">
    <h5 class="point-section__title">
      {{ $t('fleetTaskInstant.title.route') }}
    </h5>

    <div class="__container">
      <div class="point-section__wrapper">
        <draggable
          v-model="localPoints"
          class="point-section__group"
          :move="handleMovePoint"
          @change="handleDragPoint"
        >
          <div v-for="(point, index) in localPoints" :key="point.id" class="point-section__list">
            <div class="__numbering-wrapper">
              <div class="__index">{{ index + 1 }}</div>
              <button class="icon-btn __drag-icon">
                <i class="icon-outline_move" />
              </button>
              <div class="__dashed-line" />
            </div>
            <div class="__input">
              <!-- Point Input -->
              <AddressInput
                :ref="`address-input-${index}`"
                :modelValue="point"
                :disabled="isLoadingGlobal"
                fixed
                @update:modelValue="(value) => handleUpdatePoint({ index, value })"
              />

              <!-- Start Checkbox -->
              <div v-if="isShowCheckbox && index === 0" class="__checkbox">
                <McCheckbox
                  id="start-checkbox"
                  :modelValue="pointType.start"
                  :disabled="isLoadingGlobal"
                  @change="(value) => handleClickCheckbox('start', value)"
                />
                <label for="start-checkbox">{{ $t('fleetTaskInstant.action.point.start') }}</label>
              </div>

              <!-- End Checkbox -->
              <div v-if="isShowCheckbox && index === totalPoints - 1" class="__checkbox">
                <McCheckbox
                  id="end-checkbox"
                  :modelValue="pointType.end"
                  :disabled="isLoadingGlobal"
                  @change="(value) => handleClickCheckbox('end', value)"
                />
                <label for="end-checkbox">
                  {{ $t('fleetTaskInstant.action.point.end') }}
                </label>
              </div>
            </div>

            <!-- Delete Button -->
            <div v-if="!isInitial" class="__delete-action">
              <button
                class="icon-btn __delete-icon"
                :disabled="isLoadingGlobal"
                @click="handleDeletePoint({ index })"
              >
                <i class="icon-outline_trash" />
              </button>
            </div>
          </div>
        </draggable>

        <!-- Swap Button -->
        <!-- NOTE: showed if totalPoints === 2 -->
        <div v-if="isShowSwapBtn" class="__swap-icon">
          <button class="icon-btn btn" @click="handleSwapPoints">
            <i class="icon-outline_swap" />
          </button>
        </div>
      </div>
    </div>
    <!-- Add Point Button -->
    <McButton
      v-if="selectedPoints.length > 0 && selectedPoints[selectedPoints.length - 1].id !== undefined"
      block
      icon="icon-outline_plus"
      :text="$t('fleetTaskInstant.action.addDestination')"
      :disabled="isLoadingGlobal"
      @click="handleAddPoint"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import draggable from 'vuedraggable'

import cloneDeep from 'lodash/cloneDeep'
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'

import AddressInput from '../../wrapper/AddressInput'
import { EventBus } from '../../lib/event-bus.js'

export default {
  components: {
    draggable,
    McButton,
    McCheckbox,
    AddressInput,
  },
  mixins: [Helper],
  props: {
    isInitial: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      localPoints: [],
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', [
      'selectedPoints',
      'pointType',
      'isLoadingGlobal',
      'formValues',
    ]),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasUserGroupSuperUser() {
      const hasScope = this.hasAccessAll(['tms-user-group:read'])
      const isSuperUser = this.isSuperUser()

      return hasScope && isSuperUser
    },
    totalPoints() {
      return this.selectedPoints.length
    },
    isAddingPoint() {
      if (this.totalPoints === 0) {
        return false
      }

      return this.selectedPoints[this.totalPoints - 1].id === undefined
    },
    isShowSwapBtn() {
      return (
        this.totalPoints === 2 &&
        !this.isAddingPoint &&
        !(this.pointType.start || this.pointType.end)
      )
    },
    isShowCheckbox() {
      return this.totalPoints >= 2 && !this.isAddingPoint
    },
  },
  watch: {
    selectedPoints(newValue) {
      // always deep clone the selectedPoints from vuex to local state
      this.localPoints = cloneDeep(newValue)
    },
  },
  created() {
    // deep clone the selectedPoints from vuex to local state
    this.localPoints = cloneDeep(this.selectedPoints)
  },
  methods: {
    async handleAddPoint() {
      // add new input
      this.$store.dispatch('fleetTaskInstant/addSelectedPoints', { id: undefined })
      await this.$nextTick()

      // get the last input ref to focus the input
      const inputRef = this.$refs[`address-input-${this.totalPoints - 1}`][0]
      inputRef.HandleOpen()
    },

    handleUpdatePoint({ index, value }) {
      // set the point value
      this.$store.dispatch('fleetTaskInstant/setSelectedPoints', { index, value })

      // refetch nearest vehicle data
      if (index === 0 && (!this.hasUserGroupSuperUser || this.formValues.user_group_id !== null)) {
        EventBus.$emit('get-nearest-vehicle')
      }
    },

    handleDeletePoint({ index }) {
      if (this.totalPoints === 1) {
        this.$store.dispatch('fleetTaskInstant/resetSelectedPoints')
        if (this.hasUserGroupSuperUser) {
          EventBus.$emit('reset-vehicles-on-map')
        }
        return
      }

      this.$store.dispatch('fleetTaskInstant/removeSelectedPoints', { index })

      // refetch nearest vehicle data
      if (index === 0 && (!this.hasUserGroupSuperUser || this.formValues.user_group_id !== null)) {
        EventBus.$emit('get-nearest-vehicle')
      }
    },

    handleClickCheckbox(type, value) {
      this.$store.dispatch('fleetTaskInstant/setPointType', { type, value })
    },

    handleDragPoint({ moved }) {
      this.$store.dispatch('fleetTaskInstant/setAllSelectedPoints', cloneDeep(this.localPoints))

      // refetch nearest vehicle if updated index is 0
      if (
        (moved.newIndex === 0 || moved.oldIndex === 0) &&
        (!this.hasUserGroupSuperUser || this.formValues.user_group_id !== null)
      ) {
        EventBus.$emit('get-nearest-vehicle')
      }
    },

    handleMovePoint({ draggedContext, relatedContext }) {
      // cancel move if it's empty point / isLoading
      if (
        draggedContext.element.id === undefined ||
        relatedContext.element.id === undefined ||
        this.isLoadingGlobal
      ) {
        return false
      }

      const draggedIndex = draggedContext.index
      const relatedIndex = relatedContext.index

      // cancel move if it has type 'start'
      if ((draggedIndex === 0 || relatedIndex === 0) && this.pointType.start) {
        return false
      }

      // cancel move if it has type 'end'
      if (
        (draggedIndex === this.totalPoints - 1 || relatedIndex === this.totalPoints - 1) &&
        this.pointType.end
      ) {
        return false
      }

      return true
    },

    handleSwapPoints() {
      this.$store.dispatch('fleetTaskInstant/swapSelectedPoints')

      // refetch nearest vehicle
      if (!this.hasUserGroupSuperUser || this.formValues.user_group_id !== null) {
        EventBus.$emit('get-nearest-vehicle')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$total-height: calc(100vh - 165px);

.point-section {
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &.__expand {
    max-height: calc($total-height / 2);
    padding-bottom: 0;

    .__container {
      overflow-y: auto;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }
  }

  &__title {
    font-size: 18px;
    color: $mep-color-text-400;
  }

  &__wrapper {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding-top: 2px;

    .__swap-icon {
      margin: auto;
      width: 30px;

      .btn {
        height: fit-content;
        transform: rotate(90deg);
      }
    }
  }

  &__group {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__list {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: between;

    &:first-child {
      .__numbering-wrapper > .__dashed-line {
        top: 10px;
      }
    }

    &:last-child {
      .__input,
      .__delete-icon {
        padding-bottom: 0;
      }

      .__numbering-wrapper > .__dashed-line {
        height: 10px;
      }
    }

    .__numbering-wrapper {
      min-width: 20px;
      height: 100%;
      position: relative;

      .__index {
        top: 10px;
        position: absolute;
        z-index: 2;
        min-width: 20px;
        height: 20px;
        border-radius: 20px;
        color: $white;
        background-color: $mep-secondary;
        font-size: 11px;
        text-align: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .__drag-icon {
        display: none;
      }

      .__dashed-line {
        position: absolute;
        height: 100%;
        width: 50%;
        border-right: 1px dashed #99a1b1;
      }

      &:hover {
        .__index {
          display: none;
        }

        .__drag-icon {
          display: inline;
          position: absolute;
          z-index: 2;
          top: 10px;
          min-width: 20px;
          height: 20px;
        }
      }
    }

    .__input,
    .__delete-icon {
      padding-bottom: 16px;
    }

    .__input {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .__delete-action {
      min-width: 20px;
      height: 100%;
      position: relative;

      .__delete-icon {
        position: absolute;
        top: 10px;
        i:before {
          color: $red;
        }
      }
    }

    .__checkbox {
      display: flex;
      gap: 8px;
      align-items: center;

      label {
        color: $mep-color-text-400;
        font-size: 11px;
        margin: 0;
      }
    }
  }

  &__vehicle-section {
    flex-grow: 1;
    overflow: auto;
  }
}

.icon-btn {
  border: none;
  background-color: transparent;
  padding: 0px;
  margin: 0px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
}
</style>
